import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Button,
  Link, TextField, Divider, IconButton, Tooltip, Snackbar,
} from '@mui/material';
import {
  Newspaper, OpenInNew, ContentCopy, Sms, ChevronLeft, ChevronRight, Refresh, InsightsOutlined,
} from '@mui/icons-material';
import apiService from '../services/api';

const ACCENT = '#3B82F6';

const EVENT_COLORS: Record<string, string> = {
  Funding: '#10B981', 'M&A': '#F59E0B', IPO: '#A78BFA', Stealth: '#7C3AED',
  People: '#FBBF24', Product: '#38BDF8', Partnership: '#34D399', Layoffs: '#EF4444',
  Regulatory: '#F87171', Expansion: '#22D3EE', General: '#94A3B8',
};

function eventColor(e: string): string { return EVENT_COLORS[e] || '#94A3B8'; }

function todayStr(): string { return new Date().toISOString().slice(0, 10); }
function shiftDate(d: string, days: number): string {
  const dt = new Date(d + 'T00:00:00Z');
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
function timeOf(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ArticleCard({ a }: { a: any }) {
  const an = a.analysis || {};
  const col = eventColor(an.event_type);
  return (
    <Card sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
      borderLeft: `4px solid ${col}` }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
            <Chip size="small" label={an.event_type} sx={{ height: 18, fontSize: '0.62rem', bgcolor: `${col}22`, color: col, fontWeight: 'bold' }} />
            {an.amount && <Chip size="small" label={an.amount} sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#10B98122', color: '#34D399' }} />}
            {an.stage && <Chip size="small" label={an.stage} sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${ACCENT}22`, color: ACCENT }} />}
            {(an.sectors || []).map((s: string) => (
              <Chip key={s} size="small" label={s} sx={{ height: 18, fontSize: '0.58rem', bgcolor: '#ffffff12', color: 'text.secondary' }} />
            ))}
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>{timeOf(a.published_at)}</Typography>
        </Box>

        <Link href={a.url} target="_blank" rel="noopener noreferrer"
          sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.92rem', textDecorationColor: `${ACCENT}55`,
            display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { color: ACCENT } }}>
          {a.title}<OpenInNew sx={{ fontSize: 13, opacity: 0.6 }} />
        </Link>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mt: 1, p: 1, bgcolor: `${col}0c`, borderRadius: 1 }}>
          <InsightsOutlined sx={{ fontSize: 15, color: col, mt: '1px' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
            <strong style={{ color: col }}>VC impact: </strong>{an.impact}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', mt: 0.5, display: 'block' }}>
          {a.source}{an.companies?.length ? ` · ${an.companies.join(', ')}` : ''} · importance {an.importance}/100
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function IsraeliNews() {
  // Empty date = "let the backend pick the latest day with news".
  const [date, setDate] = useState<string>('');
  const [news, setNews] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // `d` undefined → backend resolves to the most recent day that has news.
  const load = useCallback(async (d?: string, forceRefresh = false) => {
    setLoading(true);
    try {
      if (forceRefresh) { setRefreshing(true); await apiService.refreshIsraeliStartups(); }
      const [n, s] = await Promise.all([
        apiService.getIsraeliNews(d),
        apiService.getIsraeliNewsSummary(d, forceRefresh),
      ]);
      setNews(n); setSummary(s);
      if (n?.date) setDate(n.date);   // sync the picker to the resolved day
      setError(null);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to load Israeli news');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  // Initial load: no date → latest day with news.
  useEffect(() => { load(); }, [load]);

  const goToDate = (d: string) => { setDate(d); load(d); };

  const copy = (txt: string, label: string) => {
    navigator.clipboard.writeText(txt).then(() => setToast(`${label} copied to clipboard`));
  };

  const isToday = date === todayStr();
  const articles = news?.articles ?? [];
  const stats = summary?.stats ?? {};

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Newspaper sx={{ color: ACCENT, fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Israeli Startup News</Typography>
            <Typography variant="caption" color="text.secondary">
              Real-time, analyzed feed — every story tagged with its VC impact. Browse any historical day.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Previous day"><span><IconButton size="small" disabled={!date} onClick={() => goToDate(shiftDate(date, -1))}><ChevronLeft /></IconButton></span></Tooltip>
          <TextField type="date" size="small" value={date}
            onChange={e => e.target.value && goToDate(e.target.value)}
            inputProps={{ max: todayStr() }} sx={{ width: 160 }} />
          <Tooltip title="Next day"><span><IconButton size="small" disabled={!date || isToday} onClick={() => goToDate(shiftDate(date, 1))}><ChevronRight /></IconButton></span></Tooltip>
          <Button size="small" variant="outlined" startIcon={refreshing ? <CircularProgress size={13} color="inherit" /> : <Refresh />}
            onClick={() => load(date || undefined, true)} disabled={refreshing || loading}
            sx={{ color: ACCENT, borderColor: `${ACCENT}55` }}>
            {isToday ? 'Refresh' : 'Re-analyze'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Daily summary card */}
      {summary && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper', border: `1px solid ${ACCENT}44`,
          background: `linear-gradient(135deg, ${ACCENT}10, transparent)` }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Daily Digest — {new Date(date + 'T00:00:00Z').toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{summary.article_count}</strong> relevant stories · <strong style={{ color: '#34D399' }}>
                    ~${summary.total_funding_m?.toLocaleString()}M</strong> disclosed in rounds &amp; stealth
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Copy SMS / WhatsApp-ready summary">
                  <Button size="small" variant="contained" startIcon={<Sms />} onClick={() => copy(summary.sms_text, 'SMS summary')}
                    sx={{ bgcolor: ACCENT, fontSize: '0.72rem', textTransform: 'none' }}>SMS / WhatsApp</Button>
                </Tooltip>
                <Tooltip title="Copy full digest (markdown)">
                  <IconButton size="small" onClick={() => copy(summary.summary_md, 'Full digest')}><ContentCopy fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* event + sector chips */}
            <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', mt: 1.5 }}>
              {Object.entries(stats.event_counts || {}).sort((a: any, b: any) => b[1] - a[1]).map(([k, v]: any) => (
                <Chip key={k} size="small" label={`${k} ${v}`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${eventColor(k)}22`, color: eventColor(k) }} />
              ))}
              {Object.entries(stats.sector_counts || {}).sort((a: any, b: any) => b[1] - a[1]).slice(0, 4).map(([k, v]: any) => (
                <Chip key={k} size="small" variant="outlined" label={`${k} ${v}`} sx={{ height: 20, fontSize: '0.62rem', color: 'text.secondary', borderColor: 'divider' }} />
              ))}
            </Box>

            {/* coverage validation */}
            {stats.coverage && !stats.coverage.complete && (
              <Alert severity="info" sx={{ mt: 1.5, py: 0, fontSize: '0.72rem' }}>
                Coverage check: {stats.coverage.sources_missing.length} feed(s) returned nothing this day
                ({stats.coverage.sources_missing.slice(0, 4).join(', ')}{stats.coverage.sources_missing.length > 4 ? '…' : ''}).
                Hit Refresh to re-pull, or verify manually.
              </Alert>
            )}
            {stats.coverage && stats.coverage.complete && (
              <Typography variant="caption" color="#34D399" sx={{ display: 'block', mt: 1 }}>
                ✓ Coverage complete — all {stats.coverage.sources_present.length} feeds contributed.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Article list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : articles.length === 0 ? (
        <Alert severity="info">No relevant Israeli startup news found for this day. Try another date or Refresh.</Alert>
      ) : (
        <>
          <Divider sx={{ mb: 2 }}><Chip label={`${articles.length} stories — ranked by importance`} size="small" /></Divider>
          {articles.map((a: any, i: number) => <ArticleCard key={i} a={a} />)}
        </>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
        News ingested every 3 hours and stored permanently — historical days are fully browsable. Impact tags are
        automated heuristics for research, not investment advice.
      </Typography>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
